import { configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import authReducer from "../features/auth/authSlice";
import oldSiteReducer from "../features/oldSite/oldSiteSlice";
import rootReducer from "../features/root/rootSlice";
import { announcementServiceApi } from "./announcementServiceApi";
import { attendanceServiceApi } from "./attendanceServiceApi";
import { authServiceApi } from "./authServiceApi";
import { employeeServiceApi } from "./employeeServiceApi";
import { employeeTrackingApi } from "./employeeTrackingApi";
import { expenseServiceApi } from "./expenseServiceApi";
import { holidayServiceApi } from "./holidayServiceApi";
import { kpiManagementServiceApi } from "./kpiManagementServiceApi";
import { leaveManagementServiceApi } from "./leaveManagementServiceApi";
import {
  cacheInvalidationMiddleware,
  invalidateAllTagsOnSignInMiddleware,
} from "./middleware/middleware";
import { notificationApi } from "./notificationApi";
import { oldSiteServiceApi } from "./oldSiteApi";
import { payrollServiceApi } from "./payrollServiceApi";
import { reportApi } from "./reportApi";
import { shiftServiceApi } from "./shiftServiceApi";
import { visitServiceApi } from "./visitServiceApi";

type RootState = ReturnType<typeof store.getState>;
type AppDispatch = typeof store.dispatch;

export const store = configureStore({
  reducer: {
    [authServiceApi.reducerPath]: authServiceApi.reducer,
    [employeeServiceApi.reducerPath]: employeeServiceApi.reducer,
    [holidayServiceApi.reducerPath]: holidayServiceApi.reducer,
    [shiftServiceApi.reducerPath]: shiftServiceApi.reducer,
    [expenseServiceApi.reducerPath]: expenseServiceApi.reducer,
    [visitServiceApi.reducerPath]: visitServiceApi.reducer,
    [attendanceServiceApi.reducerPath]: attendanceServiceApi.reducer,
    [leaveManagementServiceApi.reducerPath]: leaveManagementServiceApi.reducer,
    [announcementServiceApi.reducerPath]: announcementServiceApi.reducer,
    [employeeTrackingApi.reducerPath]: employeeTrackingApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [payrollServiceApi.reducerPath]: payrollServiceApi.reducer,
    [reportApi.reducerPath]: reportApi.reducer,
    [oldSiteServiceApi.reducerPath]: oldSiteServiceApi.reducer,
    [kpiManagementServiceApi.reducerPath]: kpiManagementServiceApi.reducer,

    root: rootReducer,
    auth: authReducer,
    oldSite: oldSiteReducer,
  },
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(authServiceApi.middleware)
      .concat(employeeServiceApi.middleware)
      .concat(holidayServiceApi.middleware)
      .concat(shiftServiceApi.middleware)
      .concat(expenseServiceApi.middleware)
      .concat(attendanceServiceApi.middleware)
      .concat(leaveManagementServiceApi.middleware)
      .concat(announcementServiceApi.middleware)
      .concat(employeeTrackingApi.middleware)
      .concat(notificationApi.middleware)
      .concat(visitServiceApi.middleware)
      .concat(payrollServiceApi.middleware)
      .concat(reportApi.middleware)
      .concat(oldSiteServiceApi.middleware)
      .concat(kpiManagementServiceApi.middleware)
      .concat(
        invalidateAllTagsOnSignInMiddleware([
          authServiceApi,
          employeeServiceApi,
          holidayServiceApi,
          shiftServiceApi,
          expenseServiceApi,
          attendanceServiceApi,
          leaveManagementServiceApi,
          announcementServiceApi,
          employeeTrackingApi,
          notificationApi,
          visitServiceApi,
          payrollServiceApi,
          reportApi,
          kpiManagementServiceApi,
        ])
      )
      .concat(cacheInvalidationMiddleware),
});

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
